import {
  AnalyticsEvent,
  BannerPromotionTileType,
  LaunchPinwheelFlowAction,
  PinwheelExperienceInput,
  ProductOfferCardActionResult,
  ProductOfferTileType,
  PromotionTileType,
  RouteNavigationAction,
  TileType,
} from '@varo/graphql-schema-types';
import { Href, ImperativeRouter, useRouter } from 'expo-router';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import useDeleteSystemBanner from 'features/home/HomeScreen/hooks/useDeleteSystemBanner/useDeleteSystemBanner';
import { usePlaidWrapperForHome } from 'features/home/HomeScreen/hooks/usePlaidWrapperForHome/usePlaidWrapperForHome';
import useUpdateBestAction from 'features/onboarding/hooks/useUpdateBestAction';
import { AllRecipients } from 'features/v2a/sender/helpers/Sender.helpers';
import { V2AContext } from 'features/v2a/sender/provider/V2AContextProvider';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GQLAnalytics,
  parseGQLAnalyticsEvent,
} from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { BrazeContentCard } from 'services/engagement/braze/types';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import useDirectDepositLoadingComponent from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import { useEngagement } from 'shared/hooks/useBraze';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import checkIfCardHasBeenAdded from 'shared/utils/addToWallet/checkIfCardHasBeenAdded';
import checkIfWalletAvailable from 'shared/utils/addToWallet/checkIfWalletAvailable';
import getProductOfferCardActionResultRouteData from 'shared/utils/getProductOfferCardActionResultRoute/getProductOfferCardActionResultRoute';
import RouteOverrides from 'shared/utils/routeOverrides';
import { Maybe } from 'shared/utils/types';

const handleProductOfferTileNavigationAction = (
  tileType: ProductOfferTileType,
  router: ImperativeRouter,
) => {
  switch (tileType) {
    case ProductOfferTileType.SavingsAccount:
      router.push('/open-savings');
      return;
    case ProductOfferTileType.ChargeCard:
      router.push('/believe/apply');
      return;
    default:
      return;
  }
};

const useHomeCardDependencies = (): HomeCardDependencies => {
  const { t: translatedText } = useTranslation('home', {
    keyPrefix: 'cards',
  });
  const { deleteBanner } = useDeleteSystemBanner();
  const router = useRouter();
  const { setSelectedRecipient } = useContext(V2AContext);
  const {
    brazeCards,
    marketingCarouselCards,
    logContentCardClicked,
    logContentCardDismissed,
    logContentCardImpression,
    removeBrazeCard,
  } = useEngagement();
  const { segmentClient } = useAnalyticsConfiguration();
  const { refreshTiles, fetching: isRefreshingTiles } = useRefreshHomeTiles([]);
  const { isFeatureEnabled } = useStatsig();
  const isV2ASenderFlowEnabledOnWeb = isFeatureEnabled(
    FeatureFlagKey.V2A_WEB_SENDER_FLOW,
  );
  const isNewSparkleTagEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_90_DAYS_NEW_SPARKLE_TAG,
  );

  const isHomeRedesignPhase1Enabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_HOME_REDESIGN_PHASE1,
  );

  const { executeUpdateBestAction: updateBestAction } = useUpdateBestAction();

  const pinwheelHomeLaunched = usePressableAnalytics({
    analyticsId: 'directDepositSwitchInitiatedHome',
  });

  const { initiatePinwheel, setIsPinwheelLoading } = usePinwheel();
  const { skipIntroScreen } = useDirectDepositLoadingComponent();
  const { refetchDdManagementFlow, handleDdManagementLinkTokenFailure } =
    useDirectDepositManagementFlow({
      // This should be always true on init
      pause: true,
    });

  const { startPlaid } = usePlaidWrapperForHome();
  const [analyticsEventProps, setAnalyticsEventProps] = useState<
    PressableAnalyticsProps | undefined
  >(undefined);
  const [brazeCardsVisible, setBrazeVisible] = useState<boolean>(true);

  const trackAnalyticsEvent = usePressableAnalytics({
    analyticsId: analyticsEventProps?.analyticsId,
    customAttributes: analyticsEventProps?.customAttributes,
  });

  useEffect(() => {
    if (analyticsEventProps && trackAnalyticsEvent) {
      trackAnalyticsEvent();
    }
  }, [analyticsEventProps, trackAnalyticsEvent]);

  useEffect(() => {
    setIsPinwheelLoading?.(isRefreshingTiles);
  }, [isRefreshingTiles, setIsPinwheelLoading]);

  useEffect(() => {
    if (isHomeRedesignPhase1Enabled && brazeCards?.[0]) {
      logContentCardImpression(brazeCards[0]);
    }
  }, [isHomeRedesignPhase1Enabled, brazeCards, logContentCardImpression]);

  useEffect(() => {
    if (isHomeRedesignPhase1Enabled) {
      marketingCarouselCards?.forEach((card) => logContentCardImpression(card));
    }
  }, [
    isHomeRedesignPhase1Enabled,
    marketingCarouselCards,
    logContentCardImpression,
  ]);

  const dependencies = useMemo<HomeCardDependencies>(
    () => ({
      goToAccount: (id: string) => {
        router.navigate({
          pathname: '/accounts/[id]',
          params: {
            id,
          },
        });
      },
      goToPromotionCard: (tileType: PromotionTileType) => {
        switch (tileType) {
          case PromotionTileType.CreditScore:
            router.push('/credit-score');
            break;
          case PromotionTileType.LinkedAccounts:
            startPlaid();
            break;
          case PromotionTileType.SetupDirectDeposit:
            initiatePinwheel({
              input: {
                experience: PinwheelExperienceInput.DirectDepositSwitch,
                skipIntroScreen,
              },
              launchBehavior: skipIntroScreen
                ? { delayAfterTokenMs: 3000 }
                : undefined,
              onOutcome: ({ success }) => {
                if (success) {
                  refreshTiles([
                    HomeTileTypeIds.PROMOTION_TILE_SETUP_DIRECT_DEPOSIT,
                  ]);

                  void refetchDdManagementFlow({
                    success,
                  });
                }
              },
              onLinkTokenError: handleDdManagementLinkTokenFailure,
            });
            pinwheelHomeLaunched();
            break;
          default:
            console.warn(`Unhandled click of tile type:`, tileType);
        }
      },
      productOfferTileClicked: (
        tileType: ProductOfferTileType,
        analyticsEvent: Maybe<GQLAnalytics>,
      ) => {
        if (analyticsEvent) {
          void segmentClient.logEvent(
            analyticsEvent.name,
            analyticsEvent.properties,
          );
        }
        handleProductOfferTileNavigationAction(tileType, router);
      },
      creditScoreTileClicked: (analyticsEvent: GQLAnalytics) => {
        void segmentClient.logEvent(
          analyticsEvent.name,
          analyticsEvent.properties,
        );
        router.push('/credit/about-credit-score');
      },
      varoToAnyoneTileClicked: (recipient: AllRecipients | null) => {
        const selectedRecipient = recipient ? recipient : undefined;
        setSelectedRecipient(selectedRecipient);
        if (isV2ASenderFlowEnabledOnWeb && !selectedRecipient) {
          router.push('/move-money/v2a');
        } else {
          router.push('/move-money/v2a/transfer');
        }
      },
      translatedText: translatedText,
      logContentCardClicked: (contentCard: BrazeContentCard) => {
        logContentCardClicked(contentCard);
      },
      logContentCardDismissed: (contentCard: BrazeContentCard) => {
        // Braze dismiss only logs the event - manually update state to remove card from UI
        // Only remove the card if it's still present to prevent errors with stale cards from the SDK
        // need to check if current card is presented to UI or not before removing
        const currentCard = brazeCards?.filter(
          (card) => card.id === contentCard.id,
        );
        if (contentCard.id && currentCard && currentCard.length === 1) {
          //this check is needed
          removeBrazeCard(contentCard.id);
        }
        logContentCardDismissed(contentCard);
      },
      logContentCardImpression: (contentCard: BrazeContentCard) => {
        logContentCardImpression(contentCard);
      },
      navigateToRoute: (action: RouteNavigationAction) => {
        const { route, analyticsEvent } = action;
        if (analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(analyticsEvent);
          void segmentClient.logEvent(name, properties);
        }
        // this is a server driven route and can't be type checked at compile time
        // cast to Href to prevent type error.
        // TODO: - JT-126254 Route overrides is temporary until backend can send correct Cruise routes
        const routeOverride = RouteOverrides(route);
        if (routeOverride !== null) {
          router.push(routeOverride);
        } else {
          router.push(route as Href);
        }
      },
      goToTopPerks: () => {
        router.navigate('/rewards');
      },
      viewRewardDetails: (
        id: string,
        analyticsEvent: Maybe<AnalyticsEvent>,
      ) => {
        if (analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(analyticsEvent);
          void segmentClient.logEvent(name, properties);
        }
        router.push({
          pathname: '/rewards/[id]',
          params: { id: id },
        });
      },
      bannerCardDismissClicked: (dismissId?: string) => {
        if (dismissId) {
          deleteBanner(dismissId);
        }
      },
      bannerPromotionTileClicked(tileType: BannerPromotionTileType) {
        switch (tileType) {
          case BannerPromotionTileType.VaroToAnyone:
            router.push('/move-money/v2a');
            break;
          default:
            console.warn(`Unhandled click of tile type:`, tileType);
        }
      },
      homeReferralTileClicked: () => {
        router.push('/myvaro/referrals' as Href);
      },

      manageExternalAccount: () => {
        router.push('/myvaro/external-accounts?tab=accounts');
      },

      brazeContentCards: brazeCardsVisible ? (brazeCards ?? []) : [],
      marketingCarouselCards: marketingCarouselCards ?? [],
      setBrazeCardsVisible: (visible: boolean) => {
        setBrazeVisible(visible);
      },
      refreshTiles: (tilesToRefresh: TileType[]) => {
        refreshTiles(tilesToRefresh);
      },
      checkIfWalletIsAvailable: (): boolean => {
        return checkIfWalletAvailable() ?? false;
      },
      checkCardSuffix: (cardSuffix: string): string | undefined => {
        return checkIfCardHasBeenAdded(cardSuffix);
      },
      handleProductOfferCardActionResult: (
        action: ProductOfferCardActionResult,
      ) => {
        const {
          route,
          analyticsEventProps,
          analyticsEvent,
          usePushNavigation,
        } = getProductOfferCardActionResultRouteData(action);

        if (analyticsEventProps) {
          setAnalyticsEventProps(analyticsEventProps);
        }

        if (analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(analyticsEvent);
          void segmentClient.logEvent(name, properties);
        }
        if (usePushNavigation) {
          router.push(route as Href);
        } else {
          router.navigate(route as Href);
        }
      },
      initiatePinwheelForNBA: (action: LaunchPinwheelFlowAction) => {
        const { shouldLaunchPinwheelSdk, analyticsEvent, id } = action;

        if (shouldLaunchPinwheelSdk) {
          initiatePinwheel({
            onOutcome: (result) => {
              if (result.success || result.hasPinwheelErrorEvent) {
                refreshTiles([HomeTileTypeIds.NEXT_BEST_ACTION_TILE]);
              }
              refetchDdManagementFlow(result);
            },
            onLinkTokenError: handleDdManagementLinkTokenFailure,
          });
          pinwheelHomeLaunched();
          void updateBestAction(id);
        }

        if (analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(analyticsEvent);
          void segmentClient.logEvent(name, properties);
        }
      },
      isNewSparkleTagEnabled,
      isHomeRedesignPhase1Enabled,
    }),
    [
      translatedText,
      brazeCards,
      marketingCarouselCards,
      brazeCardsVisible,
      router,
      startPlaid,
      initiatePinwheel,
      handleDdManagementLinkTokenFailure,
      pinwheelHomeLaunched,
      refreshTiles,
      refetchDdManagementFlow,
      segmentClient,
      setSelectedRecipient,
      isV2ASenderFlowEnabledOnWeb,
      isNewSparkleTagEnabled,
      logContentCardClicked,
      logContentCardDismissed,
      logContentCardImpression,
      deleteBanner,
      updateBestAction,
      isHomeRedesignPhase1Enabled,
      removeBrazeCard,
      skipIntroScreen,
    ],
  );

  return dependencies;
};

export default useHomeCardDependencies;
